import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Empty } from 'antd';
import { generateSeriesColors } from '@/utils/helpers';
import { getChartColors } from '@/utils/chartColors';
import { getChartThemeColors, getChartPluginTheme } from '@/utils/chartTheme';
import useTheme, { resolveTheme } from '@/hooks/stores/useTheme';
import Dropdown from '@/components/ui/Dropdown';
import { getGenderBreakdown } from '@/utils/genderAnalytics';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const DynamicVisualization = ({ company, field, dashboardData, seriesColors }) => {
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const themeMode = useTheme((s) => s.theme);
  const resolvedTheme = resolveTheme(themeMode);
  const chartTheme = useMemo(() => getChartThemeColors(), [resolvedTheme]);
  const pluginTheme = useMemo(
    () => getChartPluginTheme(chartTheme),
    [chartTheme],
  );
  
  // Extract data for this specific field
  const fieldData = dashboardData?.field_data?.[field.label];

  const [brandColor, setBrandColor] = useState(null);
  useEffect(() => {
    if(company && company?.brand_color){
      if(company?.brand_color !== null && company?.brand_color != brandColor){
        setBrandColor(company?.brand_color);
      }
    }
  }, [company]);
  
  // Reset selected university when field data changes
  useEffect(() => {
    setSelectedUniversity(null);
  }, [fieldData]);
  
  // Debug logging - only for University attended
  if (field.label === 'University attended') {
 
  }
  
  if (!fieldData || !field.visualization_type) {
    if (field.label === 'University attended') {
    }
    return null;
  }

  // Transform data based on field type
  const transformData = () => {
    if (Array.isArray(fieldData)) {
      // const colors = generateSeriesColors(fieldData.length, brandColor);
      if (field.label === 'University attended') {
        //console.log(`DynamicVisualization - Processing array data for ${field.label}:`, fieldData);
      }
      // if(brandColor && fieldData &&fieldData.length > 0){
      //   console.log('Brand Color:', brandColor);
      //   const seriesColors = generateSeriesColors(fieldData.length, brandColor);
      //   setSeriesColors(seriesColors);
      // }
      return fieldData.map((item, index) => {
        if (field.label === 'University attended') {
          // console.log(`DynamicVisualization - Processing item ${index} for ${field.label}:`, item);
        }
        // Handle different data structures
        let label, count;
        
        if (item.university) {
          // University attended data structure
          label = item.university;
          count = item.candidate_count;
          if (field.label === 'University attended') {
            // console.log(`DynamicVisualization - University data: label=${label}, count=${count}`);
          }
        } else if (item.language) {
          // Languages spoken data structure
          label = item.language;
          count = item.candidate_count;
          if (field.label === 'University attended') {
            // console.log(`DynamicVisualization - Language data: label=${label}, count=${count}`);
          }
        } else if (item.value !== undefined) {
          // Generic value structure
          label = item.value;
          count = item.count || 1;
          if (field.label === 'University attended') {
            // console.log(`DynamicVisualization - Generic data: label=${label}, count=${count}`);
          }
        } else if (typeof item === 'string') {
          // Simple string values
          label = item;
          count = 1;
          if (field.label === 'University attended') {
            // console.log(`DynamicVisualization - String data: label=${label}, count=${count}`);
          }
        } else {
          // Fallback to field label or index
          label = item[field.label.toLowerCase()] || `Item ${index + 1}`;
          count = item.candidate_count || item.count || 1;
          if (field.label === 'University attended') {
            // console.log(`DynamicVisualization - Fallback data: label=${label}, count=${count}`);
          }
        }
        
        const result = {
          key: String(index + 1),
          label: String(label),
          count: Number(count) || 1,
        };
        if (field.label === 'University attended') {
          // console.log(`DynamicVisualization - Transformed item ${index}:`, result);
        }
        return result;
      });
    }
    
    if (typeof fieldData === 'object' && fieldData !== null) {
      if (field.label === 'University attended') {
        // console.log(`DynamicVisualization - Processing object data for ${field.label}:`, fieldData);
      }
      
      // Gender only: form options from API breakdown (including 0%)
      if (field.label === 'Gender') {
        return getGenderBreakdown(fieldData).map((item) => ({
          key: item.key,
          label: item.label,
          count: item.percentage,
        }));
      }
      
      return Object.entries(fieldData).map(([key, value]) => ({
        key,
        label: key,
        count: typeof value === 'number' ? value : 1
      }));
    }
    
    // Handle simple values
    if (field.label === 'University attended') {
      // console.log(`DynamicVisualization - Processing simple data for ${field.label}:`, fieldData);
    }
    return [{
      key: '1',
      label: String(fieldData),
      count: 1
    }];
  };

  const data = transformData();
  
  if (field.label === 'University attended') {
    // console.log(`DynamicVisualization - Transformed data for ${field.label}:`, data);
  }

  if (data.length === 0) {
    if (field.label === 'University attended') {
      // console.log(`DynamicVisualization - No data to display for ${field.label}`);
    }
    return null;
  }

  // Helper function to generate colors for charts
  const generateColors = (count) => {
    if (seriesColors && seriesColors.length >= count) {
      return seriesColors.slice(0, count);
    }
    
    // Use the new chart color palette
    return getChartColors(count, brandColor);
  };

  // Render based on visualization type
  const visualization = () => {
    // Debug logging
    console.log(`DynamicVisualization - Field: ${field.label}, Visualization Type: ${field.visualization_type}, Data:`, data);
    
    // Handle listing types (listing__10, listing__20, listing__30)
    if (field.visualization_type && field.visualization_type.startsWith('listing__')) {
      // Extract limit from visualization type (e.g., listing__10 -> 10, listing__20 -> 20, listing__30 -> 30)
      const limit = parseInt(field.visualization_type.split('__').pop());
      
      // Sort data by count in descending order and take top N items
      const sortedData = [...data].sort((a, b) => b.count - a.count).slice(0, limit);
      
      // Generate title based on field label and limit
      const getListingTitle = (fieldLabel, limit) => {
        if (fieldLabel === 'University attended') {
          return `Top ${limit} Universities`;
        } else if (fieldLabel === 'Languages spoken') {
          return `Top ${limit} Spoken Languages`;
        } else {
          return `Top ${limit} ${fieldLabel}`;
        }
      };
      
      // Generate column header based on field label
      const getColumnHeader = (fieldLabel) => {
        if (fieldLabel === 'University attended') {
          return 'University';
        } else if (fieldLabel === 'Languages spoken') {
          return 'Language';
        } else {
          return fieldLabel;
        }
      };
      
      return (
        <div className="bg-card  rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="px-6 py-4 bg-muted border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-foreground">{getListingTitle(field.label, limit)}</h3>
                {selectedUniversity && selectedUniversity.value && (
                  <span className="text-sm text-muted-foreground">
                    (Filtered: {sortedData.filter(item => item.label === selectedUniversity.value).length} result)
                  </span>
                )}
              </div>
              {field.label === 'University attended' && (
                <div className="w-64">
                  <Dropdown
                    options={data.map(item => ({
                      label: item.label,
                      value: item.label
                    }))}
                    value={selectedUniversity}
                    onChange={setSelectedUniversity}
                    placeholder="Select a university"
                    searchable={true}
                    allowAll={true}
                    allLabel="All Universities"
                    className="w-full"
                    dropdownWidth="w-64"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {getColumnHeader(field.label)}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Candidates
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {(selectedUniversity && selectedUniversity.value ? 
                  sortedData.filter(item => item.label === selectedUniversity.value) : 
                  sortedData
                ).map((item, index) => (
                  <tr key={item.key || index} className="hover:bg-muted transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {item.label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {item.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    switch (field.visualization_type) {
      case 'card':
        // Skip card visualization for Disability and Gender fields
        if (field.label === 'Disability' || field.label === 'Gender') {
          return null;
        }
        
        return (
          <div className="bg-card rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              {field.label === 'Languages spoken' ? 'Top 10 Spoken Languages Of Candidates' :
              field.label === 'University attended' ? 'Top 10 Universities of Candidates' :
              field.label}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.slice(0, 6).map((item) => (
                <div key={item.key} className="bg-muted rounded-lg p-4 border">
                  <div className="text-2xl font-bold text-primary">{item.count}</div>
                  <div className="text-sm text-muted-foreground truncate">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'listing':
        // Listing types are now handled before the switch statement
        return null;

      case 'bar':
        // Sort data by count in descending order and take top 10
        const top10Data = [...data]
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);

        const barChartData = {
          labels: top10Data.map(item => item.label),
          datasets: [
            {
              label: field.label,
              data: top10Data.map(item => item.count),
              backgroundColor: generateColors(top10Data.length),
              borderColor: generateColors(top10Data.length),
              borderWidth: 1,
              borderRadius: 4,
            },
          ],
        };

        const barOptions = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
              onClick: null,
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${field.label}: ${context.parsed.y}`;
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: field.label,
                font: {
                  size: 14,
                  weight: 'bold'
                }
              },
              ticks: {
                maxRotation: 45,
                minRotation: 0
              }
            },
            y: {
              title: {
                display: true,
                text: 'Count',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              },
              beginAtZero: true
            }
          }
        };

        return (
          <div className="bg-card rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              {field.label === 'Languages spoken' ? 'Top 10 Spoken Languages Of Candidates' :
              field.label === 'University attended' ? 'Top 10 Universities of Candidates' :
              field.label}
            </h3>
            <div style={{ height: '400px' }}>
              <Bar data={barChartData} options={barOptions} />
            </div>
          </div>
        );

      case 'line':
        if (field.label === 'University attended') {
          console.log(`DynamicVisualization - Line chart data for ${field.label}:`, data);
        }

        const lineChartData = {
          labels: data.map(item => item.label),
          datasets: [
            {
              label: field.label,
              data: data.map(item => item.count),
              borderColor: brandColor || '#1890ff',
              backgroundColor: brandColor ? `${brandColor}20` : 'rgba(24, 144, 255, 0.1)',
              borderWidth: 3,
              pointBackgroundColor: '#ffffff',
              pointBorderColor: brandColor || '#1890ff',
              pointBorderWidth: 2,
              pointRadius: 6,
              pointHoverRadius: 8,
              tension: 0.4,
              fill: false,
            },
          ],
        };

        const lineOptions = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              onClick: null,
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${field.label}: ${context.parsed.y}`;
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: field.label,
                font: {
                  size: 14,
                  weight: 'bold'
                }
              },
              ticks: {
                maxRotation: 45,
                minRotation: 0
              }
            },
            y: {
              title: {
                display: true,
                text: 'Count',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              },
              beginAtZero: true
            }
          }
        };

        return (
          <div className="bg-card rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              {field.label === 'Languages spoken' ? 'Top 10 Spoken Languages Of Candidates' :
              field.label === 'University attended' ? 'Top 10 Universities of Candidates' :
              field.label}
            </h3>
            <div style={{ height: '400px' }}>
              <Line data={lineChartData} options={lineOptions} />
            </div>
          </div>
        );

      case 'pie':
        const pieChartData = {
          labels: data.map(item => item.label),
          datasets: [
            {
              data: data.map(item => item.count),
              backgroundColor: generateColors(data.length),
              borderColor: chartTheme.card,
              borderWidth: 2,
              hoverOffset: 15,
            },
          ],
        };

        const pieOptions = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
              onClick: null,
              labels: {
                ...pluginTheme.legend.labels,
                padding: 20,
                usePointStyle: true,
                generateLabels: (chart) => {
                  const chartData = chart.data;
                  if (chartData.labels.length && chartData.datasets.length) {
                    return chartData.labels.map((label, i) => {
                      const dataset = chartData.datasets[0];
                      const value = dataset.data[i];
                      const total = dataset.data.reduce((sum, val) => sum + val, 0);
                      const percentage = ((value / total) * 100).toFixed(1);
                      
                      return {
                        text: `${label} (${percentage}%)`,
                        fillStyle: dataset.backgroundColor[i],
                        strokeStyle: chartTheme.card,
                        fontColor: chartTheme.foreground,
                        lineWidth: 1,
                        pointStyle: 'circle',
                        hidden: false,
                        index: i
                      };
                    });
                  }
                  return [];
                }
              }
            },
            tooltip: {
              ...pluginTheme.tooltip,
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed;
                  const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${label}: ${value} (${percentage}%)`;
                }
              }
            }
          },
        };

        return (
          <div className="bg-card rounded-lg shadow-md border border-border p-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              {field.label === 'Languages spoken' ? 'Top 10 Spoken Languages Of Candidates' :
              field.label === 'University attended' ? 'Top 10 Universities of Candidates' :
              field.label}
            </h3>
            <div style={{ height: '400px' }}>
              <Pie key={`pie-${resolvedTheme}`} data={pieChartData} options={pieOptions} />
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <>
      {visualization()}
    </>
  );
};

export default DynamicVisualization; 